/**
 * @file       public share access component
 * @version    1.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018-2026, MindShare HDV LLC. All rights
 *             reserved.
 */

import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule }
    from '@angular/material/progress-spinner';

import { CyShareService }
    from '@app/secure/service/cyShare.service';
import { CyShareEntity }
    from '@app/secure/interface/CyShareEntity.interface';
import { CyShareRedeemResponse }
    from '@app/secure/interface/CyShareRedeemResponse.interface';
import { ERROR } from '@app/constants/Error.constant';
import { SHARE_ENTITY_TYPE }
    from '@app/constants/Share.constant';

@Component(
{
    selector: 'app-share-access',
    templateUrl: './shareAccess.component.html',
    styleUrls: ['./shareAccess.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        FormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatRadioModule,
        MatProgressSpinnerModule
    ]
})
class ShareAccessComponent implements OnInit
{
    public token: string;
    public contactMethod: 'email' | 'phone';
    public emailAddress: string;
    public phoneNumber: string;
    public accessCode: string;
    public loading: boolean;
    public errorMessage: string;
    public redeemed: boolean;
    public entities: CyShareEntity[];
    public expireDT: string;
    public noteType: number;

    /**
     * @desc   - construct the public share access screen
     * @access - public
     * @param  - route : ActivatedRoute
     *         - cyShareService : CyShareService
     * @return -
     */
    constructor
    (
        private route: ActivatedRoute,
        private cyShareService: CyShareService
    )
    {
        this.token = '';
        this.contactMethod = 'email';
        this.emailAddress = '';
        this.phoneNumber = '';
        this.accessCode = '';
        this.loading = false;
        this.errorMessage = '';
        this.redeemed = false;
        this.entities = [];
        this.expireDT = '';
        this.noteType = SHARE_ENTITY_TYPE.NOTE;
    }

    /**
     * @desc   - read the share token from the route
     * @access - public
     * @param  -
     * @return -
     */
    public ngOnInit(): void
    {
        this.token = this.route.snapshot.paramMap.get('token') || '';
        if (!this.token)
        {
            this.errorMessage = 'This share link is not valid.';
        }
    }

    /**
     * @desc   - redeem the share then load entities
     * @access - public
     * @param  -
     * @return -
     */
    public onSubmit(): void
    {
        let body;

        this.errorMessage = '';
        if (!this.token || !this.accessCode)
        {
            this.errorMessage =
                'Enter the access code from your second message.';
        }
        else if (this.contactMethod === 'email' &&
            !this.emailAddress)
        {
            this.errorMessage =
                'Enter the email address for this share.';
        }
        else if (this.contactMethod === 'phone' &&
            !this.phoneNumber)
        {
            this.errorMessage =
                'Enter the phone number for this share.';
        }
        else
        {
            this.loading = true;
            body =
            {
                token: this.token,
                accessCode: this.accessCode,
                email: this.contactMethod === 'email' ?
                    this.emailAddress : undefined,
                phone: this.contactMethod === 'phone' ?
                    this.phoneNumber : undefined
            };
            this.cyShareService.redeemShare(body).subscribe(
            {
                next: (response) =>
                {
                    if (response && response.error === ERROR.NO_ERROR)
                    {
                        this.loadEntities();
                    }
                    else
                    {
                        this.loading = false;
                        this.errorMessage =
                            this.messageForError(
                                response ? response.error : null
                            );
                    }
                },
                error: () =>
                {
                    this.loading = false;
                    this.errorMessage =
                        'Unable to open this share. Try again.';
                }
            });
        }
    }

    /**
     * @desc   - note HTML for display; drop TinyMCE placeholders
     * @access - public
     * @param  - content : note HTML from the API
     * @return - string html, or empty when there is no text
     */
    public displayNoteHtml
    (
        content?: string
    ): string
    {
        let html;
        let text;
        let result;

        html = '';
        text = '';
        result = '';

        if (content)
        {
            html = String(content);
            html = html.replace(
                /\s*data-mce-bogus="[^"]*"/gi,
                ''
            );
            text = html.replace(/<[^>]+>/g, '');
            text = text.replace(/&nbsp;/gi, ' ').trim();
            if (text)
            {
                result = html;
            }
        }

        return result;
    }

    /**
     * @desc   - load shared entities after a successful redeem
     * @access - private
     * @param  -
     * @return -
     */
    private loadEntities(): void
    {
        this.cyShareService.getSharedEntities().subscribe(
        {
            next: (response: CyShareRedeemResponse) =>
            {
                this.loading = false;
                if (response && response.error === ERROR.NO_ERROR)
                {
                    this.redeemed = true;
                    this.entities =
                        response.cyData?.entities || [];
                    this.expireDT =
                        String(response.cyData?.expireDT || '');
                }
                else
                {
                    this.errorMessage =
                        this.messageForError(
                            response ? response.error : null
                        );
                }
            },
            error: () =>
            {
                this.loading = false;
                this.errorMessage =
                    'Share opened but content could not load.';
            }
        });
    }

    /**
     * @desc   - map API error codes to user text
     * @access - private
     * @param  - errorCode : API error
     * @return - string message
     */
    private messageForError
    (
        errorCode: number
    ): string
    {
        let message;

        message = 'Unable to open this share.';
        if (errorCode === ERROR.SHARE_EXPIRED)
        {
            message = 'This share link has expired.';
        }
        else if (errorCode === ERROR.SHARE_ACCESS_DENIED)
        {
            message =
                'The contact or access code does not match.';
        }
        else if (errorCode === ERROR.DB_ITEM_NOT_FOUND)
        {
            message = 'This share link was not found.';
        }

        return message;
    }
}

export { ShareAccessComponent };
